import React, { useRef } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ApiDataWrapper, SearchField } from 'shared/base';
import { useReactRouterSyncWithUrlParam } from 'shared/base/hooks/useReactRouterSyncWithUrlParam';
import { FeatureFlag, useFeatureFlags } from 'shared/domains/featureFlag';
import { MainContainer } from 'shared/domains/platform';

import { CreateReportButton } from '@/components/reports/controls/createButton';
import { CreationTypeSelection } from '@/components/reports/controls/creationTypeSelection';
import { ReportPreview } from '@/components/reports/display/reportPreview';
import { ReportsListingTable } from '@/components/reports/display/reportsListing';
import TemplateListing from '@/components/reports/display/templateListing';
import { useReports } from '@/components/reports/hooks/useReports';
import { buildYourOwnTemplate } from '@/components/reports/templates';
import { PlatformPermissions } from '@/constants/platformPermissions';
import { useHasTeamsPermissions } from '@/hooks/useHasTeamsPermissions';
import { DashboardContentLayout } from '@/layouts/dashboardContent';

export default function ReportsListing() {
  const { featureFlags } = useFeatureFlags();
  const { creating: allowCreatingReports } = featureFlags[FeatureFlag.ALLOWED_CUSTOM_REPORTS];
  const userHasWriteAccess = useHasTeamsPermissions([PlatformPermissions.GENERAL_WRITE]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [showTemplateListing, setShowTemplateListing] = useReactRouterSyncWithUrlParam<boolean>({
    defaultValue: false,
    paramKey: 'showTemplateListing',
  });
  const [previewTemplate, setPreviewTemplate] = useReactRouterSyncWithUrlParam<string | undefined>({
    defaultValue: undefined,
    paramKey: 'previewTemplate',
  });

  const {
    handlePrivateChangeSort,
    handlePublicChangeSort,
    hasPrivateReports,
    hasPublicReports,
    privatePerPage,
    privateReportsData,
    privateReportsIsError,
    privateReportsIsLoading,
    privateSortBy,
    privateSortOrder,
    privateTablePage,
    publicPerPage,
    publicReportsData,
    publicReportsIsError,
    publicReportsIsLoading,
    publicSortBy,
    publicSortOrder,
    publicTablePage,
    searchTerm,
    setPrivatePerPage,
    setPrivateTablePage,
    setPublicPerPage,
    setPublicTablePage,
    setSearchTerm,
  } = useReports();

  const showCreateButton =
    userHasWriteAccess &&
    (hasPrivateReports || hasPublicReports) &&
    allowCreatingReports &&
    !previewTemplate &&
    !showTemplateListing;

  const handleSelectTemplate = async (templateId?: string) => {
    setPreviewTemplate(templateId);
    containerRef?.current?.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  };

  const handleShowTemplates = () => {
    setShowTemplateListing(true);
  };

  const handleCreateFromScratch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setPreviewTemplate(buildYourOwnTemplate.id);
  };

  return (
    <DashboardContentLayout
      actions={[
        showCreateButton && (
          <CreateReportButton
            handleCreateFromScratch={handleCreateFromScratch}
            handleShowTemplates={handleShowTemplates}
          />
        ),
      ]}
      disableGrid={true}
      sx={{ overflowX: 'hidden', position: 'relative' }}
      title="Reports"
    >
      <MainContainer ref={containerRef}>
        {previewTemplate ? (
          <ReportPreview
            handleBackAction={() => setPreviewTemplate(undefined)}
            templateId={previewTemplate}
          />
        ) : showTemplateListing || !allowCreatingReports ? (
          <Box>
            {allowCreatingReports && (
              <Button
                color="secondary"
                onClick={() => setShowTemplateListing(false)}
                startIcon={<ArrowBackIcon color="secondary" />}
                sx={{ width: 'fit-content' }}
                variant="outlined"
              >
                Back
              </Button>
            )}
            {allowCreatingReports && (
              <Typography mb={3} mt={2} variant="h5">
                Choose report template
              </Typography>
            )}
            <TemplateListing handleSelectTemplate={handleSelectTemplate} />
          </Box>
        ) : (
          <Stack gap={2}>
            <SearchField
              fullWidth={true}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm('')}
              placeholder="Search by report name"
              size="small"
              value={searchTerm}
            />
            {(hasPublicReports || !hasPrivateReports) && (
              <ApiDataWrapper
                data={publicReportsData?.data}
                emptyAction={
                  <Stack gap={2} p={1}>
                    {userHasWriteAccess && (
                      <CreationTypeSelection
                        handleCreateFromScratch={handleCreateFromScratch}
                        handleShowTemplates={handleShowTemplates}
                        variant="row"
                      />
                    )}
                  </Stack>
                }
                emptyCondensed={true}
                emptyMessage=""
                emptyTitle={'Create a report'}
                isEmpty={publicReportsData?.data?.metadata?.total_count === 0}
                isError={publicReportsIsError}
              >
                {(data) => (
                  <ReportsListingTable
                    handleChangeSort={handlePublicChangeSort}
                    isError={publicReportsIsError}
                    isLoading={publicReportsIsLoading}
                    metadata={data?.metadata}
                    perPage={publicPerPage}
                    reports={data?.reports}
                    setPerPage={setPublicPerPage}
                    setTablePage={setPublicTablePage}
                    sortBy={publicSortBy}
                    sortOrder={publicSortOrder}
                    tablePage={publicTablePage}
                  />
                )}
              </ApiDataWrapper>
            )}
            {hasPrivateReports && (
              <>
                <Typography variant="h6">Private Reports</Typography>
                <ReportsListingTable
                  handleChangeSort={handlePrivateChangeSort}
                  isError={privateReportsIsError}
                  isLoading={privateReportsIsLoading}
                  metadata={privateReportsData!.data.metadata!}
                  perPage={privatePerPage}
                  reports={privateReportsData!.data.reports!}
                  setPerPage={setPrivatePerPage}
                  setTablePage={setPrivateTablePage}
                  sortBy={privateSortBy}
                  sortOrder={privateSortOrder}
                  tablePage={privateTablePage}
                />
              </>
            )}
          </Stack>
        )}
      </MainContainer>
    </DashboardContentLayout>
  );
}
